<script lang="ts">
    import get from 'lodash/get';
    import { trackMultipleChoiceClick } from '$lib/global/tracking';
    import { createExternalUrl, getColorByLightness, personalizeText, getColor } from '$lib/helper';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { page } from '$app/stores';
    import MultipleChoiceQuestion from '$lib/components/_DisplayComponents/MultipleChoiceQuestion/MultipleChoiceQuestion.svelte';
    import { DEFAULT_LAYOUT } from '$lib/components/constants.js';

    // Types
    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { Field } from '$lib/types/Field';
    import type { Language } from '$lib/types/Language';
    import type { MultipleChoiceQuestionContent } from '$lib/types/Components/MultipleChoiceQuestion/MultipleChoiceQuestionContent';
    import type { MultipleChoiceAnswer } from '$lib/types/tracking';

    export let id = '';
    export let content: MultipleChoiceQuestionContent;
    export let childComponents: Component[] = [];
    export let fields: Field[] = [];
    export let theme: Theme;
    export let nextPage = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'de';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { pageIdSlugMap },
    } = $page;

    const button = childComponents.find((child) => child.componentType === 'button');
    const btnBgColor = getColor(
        get(button, 'content.backgroundColor'),
        theme.buttonBackgroundColor,
    );
    const btnColor = getColor(get(button, 'content.color'), getColorByLightness(btnBgColor));

    const btnDraft = get(button, 'content.wysiwyg');
    const btnProps = {
        btnId: get(button, '_id'),
        btnDraft: btnDraft
            ? personalizeText(btnDraft, trackingProperties, campaignId, language)
            : undefined,
        btnBgColor,
        btnColor,
        btnSize: get(button, 'content.size'),
        btnAnimation: get(button, 'content.animation', ''),
        btnAlign: get(button, 'content.align', 'center'),
    };

    const personalizedText = personalizeText(
        content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );

    // Navigation
    const regularNextPage = get(button, 'content.linking.pageId', nextPage);
    const externalLink = get(button, 'content.linking.url');
    const isExternal = !get(button, 'content.linking.isInternal', true) && !!externalLink;
    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    const answers = childComponents
        .filter((child) => child.componentType === 'questionTextAnswer')
        .map((answer) => {
            return {
                id: answer._id,
                color: getColor(get(answer, 'content.color'), theme.fontColor),
                selectedColor: btnBgColor,
                bgColor: getColor(
                    get(answer, 'content.backgroundColor'),
                    theme.formFieldBackgroundColor,
                ),
                showEmoji: get(answer, 'content.showIcon', false),
                emoji: get(answer, 'content.icon', ':smiley:'),
                draft: personalizeText(
                    answer.content.wysiwyg,
                    trackingProperties,
                    campaignId,
                    language,
                ),
                plainText: get(answer, 'content.plainText', ''),
            };
        });

    const handleSubmit = (answerIds: string[]) => {
        const selectedAnswers = answerIds
            .map((id) => {
                return answers.find((answer) => answer.id === id);
            })
            .filter((answer): answer is MultipleChoiceAnswer => !!answer);

        // Tracking
        trackMultipleChoiceClick({
            answers: selectedAnswers,
            question: { id, content },
            fields,
            button,
            campaignId,
        });

        // Navigate to external page
        if (isExternal) {
            return nextPageNavigator.goExternal(createExternalUrl(externalLink));
        }

        return nextPageNavigator.go(regularNextPage);
    };

    const handleMouseEnter = () => {
        nextPageNavigator.preload(regularNextPage, isExternal);
    };
</script>

<svelte:component
    this={MultipleChoiceQuestion}
    draft={personalizedText}
    align={content.align}
    color={getColor(content.color, theme.fontColor)}
    box={content.box}
    onSubmit={handleSubmit}
    onMouseEnter={handleMouseEnter}
    {transitionDelay}
    {transitionEffect}
    {answers}
    {layout}
    {...btnProps}
/>
